import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Users, LogOut, Boxes, ListTodo, Activity, BarChart3, Shield, Map, BookOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../lib/helpers";
import { NotificationBell } from "./NotificationBell";
import { ProfileDialog } from "./ProfileDialog";
import { MobileNav } from "./MobileNav";

const links = [
  { to: "/", icon: LayoutDashboard, label: "Panel", id: "panel", end: true },
  { to: "/my-tasks", icon: ListTodo, label: "Mis tareas", id: "mis-tareas" },
  { to: "/boards", icon: KanbanSquare, label: "Tableros", id: "tableros" },
  { to: "/roadmap", icon: Map, label: "Roadmap", id: "roadmap" },
  { to: "/activity", icon: Activity, label: "Actividad", id: "actividad" },
  { to: "/reports", icon: BarChart3, label: "Informes", id: "informes" },
  { to: "/team", icon: Users, label: "Equipo", id: "equipo" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    let gPressed = false;
    let timer;
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target.isContentEditable) return;
      if (e.key === "g") {
        gPressed = true;
        clearTimeout(timer);
        timer = setTimeout(() => { gPressed = false; }, 800);
        return;
      }
      if (gPressed) {
        const map = { p: "/", m: "/my-tasks", t: "/boards", a: "/activity", i: "/reports" };
        if (map[e.key]) navigate(map[e.key]);
        gPressed = false;
      } else if (e.key === "?") {
        setShowProfile((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex">
      <aside className="hidden md:flex w-[72px] lg:w-64 shrink-0 border-r border-white/10 flex-col bg-[#0C0C0E]">
        <div className="h-16 flex items-center gap-2 px-4 lg:px-6 border-b border-white/10">
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center hover:scale-110 hover:rotate-6 transition-transform cursor-pointer">
            <Boxes className="h-5 w-5 text-white" />
          </div>
          <span className="hidden lg:block font-display font-extrabold text-lg tracking-tight">Portal GA</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/15 text-primary" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <l.icon className="h-5 w-5 shrink-0" />
              <span className="hidden lg:block">{l.label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-[color,background-color,transform] hover:translate-x-0.5 ${
                  isActive ? "bg-primary/15 text-primary shadow-[inset_3px_0_0_0_currentColor]" : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }>
              <Shield className="h-5 w-5 shrink-0" />
              <span className="hidden lg:block">Administración</span>
            </NavLink>
          )}
          <a href="/guias/guia-de-uso.pdf" target="_blank" rel="noreferrer" data-testid="nav-user-guide"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-[color,background-color,transform] hover:translate-x-0.5 text-zinc-400 hover:text-white hover:bg-white/5">
            <BookOpen className="h-5 w-5 shrink-0" />
            <span className="hidden lg:block">Guía de uso</span>
          </a>
        </nav>
        <NotificationBell />
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-1 lg:px-2 py-2">
            <button onClick={() => setShowProfile(true)} data-testid="open-profile" className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
              <Avatar user={user} size={36} />
              <div className="hidden lg:block min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-zinc-400 hover:text-primary transition-colors"
              title="Cerrar sesión"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-hidden">
        <Outlet />
      </main>
      <MobileNav user={user} onOpenProfile={() => setShowProfile(true)} onLogout={handleLogout} />
      {showProfile && <ProfileDialog user={user} onClose={() => setShowProfile(false)} />}
    </div>
  );
}
